import {
  FORMAT_ATTR,
  LAZY_ATTR,
  LAZY_ROOT_MARGIN,
  MIN_REFRESH_SECS,
  MIN_REFRESH_SECS_TEST,
  REFRESH_ATTR,
  SLOT_ATTR,
  TEST_ATTR,
} from "./config";
import { consentReady, currentConsent } from "./consent";
import { getUserHash } from "./identity";
import { buildClickHref, buildIframeSrcdoc, isMediation, sandboxFor } from "./render";
import { fetchAd, fireBeacon, trackEvent } from "./transport";
import type { ServeResponse } from "./types";
import { ViewabilityWatcher } from "./viewability";

export interface SlotOptions {
  el: HTMLElement;
  key: string;
  base: string;
  watcher: ViewabilityWatcher;
}

// SlotController owns the lifecycle of one ad placement: fetch → render →
// viewable → impression → optional refresh. It is the only place that
// touches the slot's DOM children, so destroy() guarantees no leaks: the
// AbortController kills in-flight fetches, the timers are cleared, and the
// IntersectionObserver entries are released.
export class SlotController {
  readonly el: HTMLElement;
  private readonly key: string;
  private readonly base: string;
  private readonly watcher: ViewabilityWatcher;
  private readonly slotID: string;
  private readonly format: string | null;
  private readonly refreshAttrSecs: number;
  private refreshSecs: number;
  private refreshSeq = 0;
  private readonly lazy: boolean;
  private readonly isTest: boolean;

  private iframe: HTMLIFrameElement | null = null;
  private current: ServeResponse | null = null;
  private impressionFired = false;
  private fetchCtl: AbortController | null = null;
  private refreshTimer = 0;
  private lazyObserver: IntersectionObserver | null = null;
  private destroyed = false;
  private paused = false;

  constructor(o: SlotOptions) {
    this.el = o.el;
    this.key = o.key;
    this.base = o.base;
    this.watcher = o.watcher;
    this.slotID = o.el.getAttribute(SLOT_ATTR) ?? "";
    this.format = o.el.getAttribute(FORMAT_ATTR);
    const raw = parseInt(o.el.getAttribute(REFRESH_ATTR) ?? "0", 10);
    this.refreshAttrSecs = Number.isFinite(raw) && raw > 0 ? raw : 0;
    this.refreshSecs = this.refreshAttrSecs;
    this.lazy = o.el.hasAttribute(LAZY_ATTR);
    this.isTest = o.el.hasAttribute(TEST_ATTR);
  }

  mount(): void {
    if (this.destroyed) return;
    if (!this.slotID) return;
    if (this.lazy) {
      this.lazyObserver = new IntersectionObserver(
        (records) => {
          for (const r of records) {
            if (r.isIntersecting) {
              this.disposeLazyObserver();
              void this.load();
              return;
            }
          }
        },
        { rootMargin: LAZY_ROOT_MARGIN },
      );
      this.lazyObserver.observe(this.el);
    } else {
      void this.load();
    }
  }

  refresh(): void {
    if (this.destroyed) return;
    void this.load();
  }

  setPaused(p: boolean): void {
    this.paused = p;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.fetchCtl) {
      this.fetchCtl.abort();
      this.fetchCtl = null;
    }
    if (this.refreshTimer !== 0) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = 0;
    }
    this.disposeLazyObserver();
    this.teardownIframe();
  }

  private async load(): Promise<void> {
    if (this.destroyed || this.paused) return;
    if (this.fetchCtl) this.fetchCtl.abort();
    const ctl = new AbortController();
    this.fetchCtl = ctl;
    try {
      await consentReady();
      const consent = currentConsent();
      const resp = await fetchAd({
        base: this.base,
        key: this.key,
        slot: this.slotID,
        user: consent.storage ? getUserHash() : "",
        fmt: this.format ?? undefined,
        refreshSeq: this.refreshSeq,
        gdpr: consent.gdpr,
        consentString: consent.tcString,
        signal: ctl.signal,
      });
      if (this.destroyed || ctl.signal.aborted) return;
      this.current = resp;
      this.refreshSecs = this.resolveRefreshSecs(resp);
      this.impressionFired = false;
      this.render(resp);
      this.watcher.observe(this.el, () => this.onViewable());
      this.scheduleRefresh();
    } catch (err) {
      if ((err as { name?: string } | null)?.name === "AbortError") return;
      // The tag MUST never throw into the page. We log to console for
      // local debugging only; production deployments suppress this via
      // browser console levels.
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[adpluga] fetch failed", err);
      }
    } finally {
      if (this.fetchCtl === ctl) this.fetchCtl = null;
    }
  }

  // The slot's rotation cadence is configured by the publisher and published
  // on the serve response; the data attribute stays supported as an explicit
  // per-element override. Either way the MIN_REFRESH_SECS floor applies.
  private resolveRefreshSecs(resp: ServeResponse): number {
    const floor =
      resp.ad.test === true || this.isTest ? MIN_REFRESH_SECS_TEST : MIN_REFRESH_SECS;
    const secs = this.refreshAttrSecs > 0 ? this.refreshAttrSecs : resp.refresh_after_seconds ?? 0;
    return secs > 0 ? Math.max(secs, floor) : 0;
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer !== 0) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = 0;
    }
    if (this.refreshSecs <= 0) return;
    this.refreshTimer = window.setTimeout(
      () => this.rotate(),
      this.refreshSecs * 1000,
    );
  }

  // Rotating a hidden tab would spend a decision on an impression the MRC
  // guidelines treat as non-viewable, so wait for the tab to come back and
  // re-arm on the same cadence instead.
  private rotate(): void {
    if (this.destroyed || this.paused) return;
    if (typeof document !== "undefined" && document.hidden) {
      this.scheduleRefresh();
      return;
    }
    this.refreshSeq += 1;
    this.refresh();
  }

  private render(resp: ServeResponse): void {
    const w = resp.ad.width > 0 ? resp.ad.width : 300;
    const h = resp.ad.height > 0 ? resp.ad.height : 250;
    this.el.style.minWidth = w + "px";
    this.el.style.minHeight = h + "px";

    const iframe = document.createElement("iframe");
    iframe.setAttribute("sandbox", sandboxFor(resp.source));
    iframe.setAttribute("scrolling", "no");
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("referrerpolicy", "no-referrer");
    iframe.setAttribute("loading", this.lazy ? "lazy" : "eager");
    iframe.setAttribute("aria-label", "Advertisement");
    iframe.style.cssText =
      "display:block;border:0;margin:0;padding:0;width:" +
      w +
      "px;height:" +
      h +
      "px;background:transparent";
    const clickHref = buildClickHref(resp.click_url, resp.ad.landing_url);
    iframe.srcdoc = buildIframeSrcdoc(
      resp.ad,
      clickHref,
      this.isTest || resp.ad.test === true,
      isMediation(resp.source),
    );

    this.teardownIframe();
    this.el.appendChild(iframe);
    this.iframe = iframe;
  }

  private teardownIframe(): void {
    this.watcher.unobserve(this.el);
    if (this.iframe) {
      this.iframe.remove();
      this.iframe = null;
    }
  }

  private disposeLazyObserver(): void {
    if (this.lazyObserver) {
      this.lazyObserver.disconnect();
      this.lazyObserver = null;
    }
  }

  private onViewable(): void {
    if (this.impressionFired || !this.current || this.destroyed) return;
    this.impressionFired = true;
    // Mediation fills carry no AdPluga track token: the billable impression is
    // reported to the bidder by firing its burl once here. First-party fills
    // report the impression to /track instead.
    const billing = this.current.ad.billing_url;
    if (billing) {
      fireBeacon(billing);
    }
    for (const url of this.current.ad.impression_trackers ?? []) fireBeacon(url);
    if (this.current.track_token) {
      trackEvent(this.base, {
        token: this.current.track_token,
        event: "impression",
      });
    }
    this.watcher.unobserve(this.el);
  }
}
